import {
  DEFAULT_ZONE_FILTERS,
  ZoneManagementFiltersType,
} from '@components/cockpit/panels/zoneManagement/ZoneManagementFilters.types';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAllZonesByManagementFilters } from '@redux/config/config.selectors';
import { ActionDispatch, createContext, PropsWithChildren, useContext, useMemo, useReducer } from 'react';

import { Zone } from '@/types/config/config.types';

type ZoneManagementContextType = {
  filters: ZoneManagementFiltersType;
  setFilters: ActionDispatch<[newFilters: Partial<ZoneManagementFiltersType>]>;
  resetFilters: () => void;
  zones: Zone[];
};

export const ZoneManagementContext = createContext<ZoneManagementContextType | null>(null);

export const ZoneManagementContextProvider = ({ children }: Readonly<PropsWithChildren>) => {
  const [filters, setFilters] = useReducer(
    (filters: ZoneManagementFiltersType, newFilters: Partial<ZoneManagementFiltersType>) => ({
      ...filters,
      ...newFilters,
    }),
    DEFAULT_ZONE_FILTERS,
  );

  const zones = useAppSelector((state) => selectAllZonesByManagementFilters(state, filters));

  const resetFilters = () => {
    setFilters(DEFAULT_ZONE_FILTERS);
  };

  const values = useMemo(() => ({ filters, setFilters, resetFilters, zones }), [filters, zones]);

  return <ZoneManagementContext value={values}>{children}</ZoneManagementContext>;
};

export const useZoneManagementContext = () => {
  const context = useContext(ZoneManagementContext);
  if (!context) {
    throw new Error('useZoneManagementContext must be used within a ZoneManagementContextProvider');
  }
  return context;
};
